import _ from 'lodash';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { DEFAULT_DECIMAL_SCALE } from 'constants/number';

const sortArray = array =>
  _.orderBy(
    array,
    [val => (_.isObjectLike(val) && !Array.isArray(val) ? Object.values(val)[0] : val)],
    ['asc'],
  );

const isEmpty = obj => {
  return _.isNil(obj) || obj === '' || (Array.isArray(obj) && obj.length === 0);
};

const isPrimitive = arg => {
  return arg !== Object(arg);
};

/**
 * A select's form value is the raw enum value until the user picks an option, then the option
 * object; this reads the enum value from either, null when empty.
 */
export const getFormOptionValue = value =>
  _.isPlainObject(value) ? (value.value ?? null) : (value ?? null);

export const isBooleanToggleField = fieldType =>
  fieldType === FieldType.SWITCH || fieldType === FieldType.CHECKBOX;

export const equalCustomizer = (obj, other) => {
  const isBothPrimitive = isPrimitive(obj) && isPrimitive(other);

  const isObjEmptyArray = Array.isArray(obj) && obj.length === 0;
  const isOtherEmptyArray = Array.isArray(other) && other.length === 0;

  if (isBothPrimitive && _.toString(obj) === _.toString(other)) {
    return true;
  }

  if (!obj && !other) {
    return isEmpty(obj) === isEmpty(other);
  }

  if (isObjEmptyArray || isOtherEmptyArray) {
    return isEmpty(obj) === isEmpty(other);
  }

  const isBothObjectLike = _.isObjectLike(obj) && _.isObjectLike(other);

  if (isBothObjectLike) {
    const isSameSize = _.size(obj) === _.size(other);

    const areBothArrays = Array.isArray(obj) && Array.isArray(other);
    const formattedObj = areBothArrays ? sortArray(obj) : obj;
    const formattedOther = areBothArrays ? sortArray(other) : other;

    return (
      isSameSize &&
      _.every(
        formattedObj,
        (value, key) =>
          _.has(formattedOther, key) &&
          _.isEqualWith(value, _.get(formattedOther, key), equalCustomizer),
      )
    );
  }
};

export const fixDecimalScaleInForm = (form, fieldsConfig) => {
  if (!fieldsConfig) {
    return form;
  }

  return _.mapValues(form, (value, name) => {
    const { type, decimalScale = DEFAULT_DECIMAL_SCALE } = fieldsConfig[name] || {};

    if (!parseFloat(value)) {
      return value;
    }

    switch (type) {
      case FieldInputType.CURRENCY:
      case FieldInputType.FLOAT:
        return parseFloat(value).toFixed(decimalScale);

      default:
        return value;
    }
  });
};

const coerceBooleanTogglesToFalse = (form, fieldsConfig) => {
  if (!fieldsConfig) {
    return form;
  }

  return _.mapValues(form, (value, name) => {
    const { fieldType } = fieldsConfig[name] || {};

    return isBooleanToggleField(fieldType) && _.isNil(value) ? false : value;
  });
};

// A `noObserve` field (e.g. a UI-only calculator derived from other fields, never itself
// persisted) must not count toward form dirtiness -- mirrors the `observableFieldsNames`
// exclusion already applied to the pending-changes summary (see utils/loanManagement.js).
const omitNoObserveFields = (form, fieldsConfig) => {
  if (!fieldsConfig) {
    return form;
  }

  return _.omitBy(form, (value, name) => fieldsConfig[name]?.noObserve);
};

// Need to fix decimal scale for fields when comparing because it can be different for some cases
export const checkIsFormChanged = ({ initialForm, currentForm, fieldsConfig }) => {
  const initialFormWithFixedDecimalScale = omitNoObserveFields(
    coerceBooleanTogglesToFalse(fixDecimalScaleInForm(initialForm, fieldsConfig), fieldsConfig),
    fieldsConfig,
  );
  const formWithFixedDecimalScale = omitNoObserveFields(
    coerceBooleanTogglesToFalse(fixDecimalScaleInForm(currentForm, fieldsConfig), fieldsConfig),
    fieldsConfig,
  );

  return !_.isEqualWith(
    initialFormWithFixedDecimalScale,
    formWithFixedDecimalScale,
    equalCustomizer,
  );
};

export const omitMissedInConfigFields = ({ form, fieldsNames }) => _.pick(form, fieldsNames);

export const normalizeFormPath = path => _.toPath(path).join('.');
